/**
 * TalentProfile — /me/talent-profile
 *
 * Student-facing page to opt in to the Reverse Job Board.
 *   • "Currently at university" → University + current-degree autocomplete
 *   • "Applying to university" → target degrees prefilled from the
 *     student's shortlist (removable + can add more via autocomplete)
 *   • Also captures: skills×3, sectors×5, languages×5, LinkedIn,
 *     notable extras (chips), 200-char bio.
 * Anonymized until BOTH the student and the firm mark interest.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import UniversityAutocomplete from "@/components/app/UniversityAutocomplete";
import DegreeAutocomplete from "@/components/app/DegreeAutocomplete";
import JourneyTicket from "@/components/app/JourneyTicket";
import MetroTicket from "@/components/app/MetroTicket";
import CityAutocomplete from "@/components/app/CityAutocomplete";
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  ShieldCheck,
  Handshake,
  Trash2,
  GraduationCap,
  Linkedin,
  Plus,
} from "lucide-react";


const SECTOR_LABELS = {
  consulting: "Consulting",
  finance: "Finance",
  tech: "Tech",
  law: "Law",
  engineering: "Engineering",
  medicine: "Medicine",
  policy_gov: "Policy / Gov",
  media_creative: "Media / Creative",
  science_research: "Science / Research",
  energy_climate: "Energy / Climate",
  retail_fmcg: "Retail / FMCG",
  aerospace_defence: "Aerospace / Defence",
  startup: "Startup",
  nonprofit: "Nonprofit",
  education: "Education",
};


const NOTABLE_LABELS = {
  // Work experience & career
  internship_experience: "Internship experience",
  part_time_job_2yr_plus: "Part-time job 2yr+",
  freelance_client_work: "Freelance client work",
  startup_founder: "Founded a startup",
  family_business_experience: "Family business",
  // Research & academic
  published_research: "Published research",
  research_assistant: "Research assistant",
  academic_scholarship: "Academic scholarship",
  olympiad_medallist: "Olympiad medallist",
  language_olympiad: "Language olympiad",
  // Competitions & prizes
  hackathon_prize: "Hackathon prize",
  coding_contest: "Coding contest",
  case_competition_winner: "Case-comp winner",
  debate_champion: "Debate champion",
  moot_court_finalist: "Moot court finalist",
  // Leadership & community
  society_president: "Society president",
  student_union_officer: "SU officer",
  editor_student_paper: "Editor, student paper",
  peer_mentor: "Peer mentor",
  volunteering_1yr_plus: "Volunteering 1yr+",
  // Sports
  sports_captain: "Sports captain",
  national_team_athlete: "National team athlete",
  duke_of_edinburgh_gold: "DofE Gold",
  // Arts & performance
  musical_ensemble_principal: "Music ensemble principal",
  theatre_lead_role: "Theatre lead role",
  published_artist_writer: "Published artist / writer",
  // Life & service
  military_service: "Military service",
  first_generation_uni: "First-gen uni",
};


const DIVERSITY_LABELS = {
  // Gender
  gender_woman: "Woman",
  gender_nonbinary: "Non-binary",
  gender_trans: "Trans",
  // Ethnicity
  ethnicity_black: "Black",
  ethnicity_asian_south: "Asian (South)",
  ethnicity_asian_east_southeast: "Asian (East / SE)",
  ethnicity_mixed: "Mixed heritage",
  ethnicity_hispanic_latino: "Hispanic / Latino",
  ethnicity_mena: "Middle Eastern / N. African",
  ethnicity_indigenous_first_nations: "Indigenous / First Nations",
  // Access / background
  first_gen_uni: "First-gen uni",
  state_school_educated: "State-school educated",
  low_income_household: "Low-income household",
  care_leaver: "Care leaver",
  refugee_asylum: "Refugee / asylum background",
  // Life / identity
  lgbtq_plus: "LGBTQ+",
  disability: "Disability",
  neurodivergent: "Neurodivergent",
  veteran_or_military_family: "Veteran / military family",
  mature_student: "Mature student",
  carer_responsibilities: "Carer responsibilities",
  faith_religion: "Faith / religion",
};


function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}


export default function TalentProfile() {
  const { user, loading: authLoading, openSignIn } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [allowedSectors, setAllowedSectors] = useState([]);
  const [subjectFamilies, setSubjectFamilies] = useState([]); // [{key,label}]
  const [allowedNotable, setAllowedNotable] = useState([]);
  const [allowedDiversity, setAllowedDiversity] = useState([]);
  const [allowedLevels, setAllowedLevels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [draftingBio, setDraftingBio] = useState(false);
  const [interests, setInterests] = useState(null);
  const [invites, setInvites] = useState([]);
  const [uniObj, setUniObj] = useState(null);      // {id, name}
  const [currentDegObj, setCurrentDegObj] = useState(null); // {id, title}
  const [targetDegs, setTargetDegs] = useState([]); // [{id, title, university?: {id,name}}]
  const [targetCities, setTargetCities] = useState([]); // ["Oxford", "London"]
  const [shortlistLoaded, setShortlistLoaded] = useState(false);
  // Distinct unis from the student's shortlist — used as quick-pick
  // chips on the "add another target degree" university input so
  // students don't have to re-search the same unis they've already
  // curated. Always accumulated even when the target-degrees prefill
  // step was skipped, because the chips are useful either way.
  const [shortlistUnis, setShortlistUnis] = useState([]); // [{id, name}]
  const [ticket, setTicket] = useState(null);
  const [shareState, setShareState] = useState(null);

  const [form, setForm] = useState({
    status: "current",
    graduation_year: "",
    skills: ["", "", ""],
    sectors: [],
    subject_families: [],
    languages: [{ lang: "", level: "fluent" }],
    linkedin_url: "",
    notable_extras: [],
    diversity_tags: [],
    diversity_reachout_opt_in: false,
    bio: "",
  });

  // ── Load profile + interests + allowed enums ────────────────────
  useEffect(() => {
    if (authLoading) return;
    if (!user) { openSignIn?.(); return; }
    // Fetch the journey ticket in parallel — silent-fail so a missing
    // profile doesn't break the talent page.
    api.get("/ticket/me")
      .then(({ data }) => setTicket(data || null))
      .catch(() => setTicket(null));
    (async () => {
      try {
        const [{ data: p }, { data: i }] = await Promise.all([
          api.get("/talent/profile/me"),
          api.get("/talent/interests/mine"),
        ]);
        setData(p);
        // Fetch broadcast inbox — silent-fail so a missing endpoint
        // or 401 doesn't break the profile load.
        api.get("/me/broadcasts")
          .then(({ data: b }) => setInvites(b?.items || []))
          .catch(() => setInvites([]));
        setAllowedSectors(p.allowed_sectors || []);
        setAllowedNotable(p.allowed_notable_extras || []);
        setAllowedDiversity(p.allowed_diversity_tags || []);
        setAllowedLevels(p.allowed_language_levels || ["native","fluent","working","basic"]);
        // /talent/profile/me exposes an `allowed_sectors` shortcut
        // but not the family enum — hit the shared enums route for
        // that.
        try {
          const { data: e } = await api.get("/talent/enums");
          setSubjectFamilies(e?.subject_families || []);
        } catch { /* silent */ }
        setInterests(i);
        if (p.profile) {
          const pr = p.profile;
          setForm({
            status: pr.status || "current",
            graduation_year: pr.graduation_year || "",
            skills: [...(pr.skills || []), "", "", ""].slice(0, 3),
            sectors: pr.sectors || [],
            subject_families: pr.subject_families || [],
            languages: (pr.languages && pr.languages.length ? pr.languages : [{ lang: "", level: "fluent" }]).slice(0, 5),
            linkedin_url: pr.linkedin_url || "",
            notable_extras: pr.notable_extras || [],
            diversity_tags: pr.diversity_tags || [],
            diversity_reachout_opt_in: !!pr.diversity_reachout_opt_in,
            bio: pr.bio || "",
          });
          // Hydrate uni + degree labels
          if (pr.university_id) {
            try {
              const { data: u } = await api.get(`/universities/${pr.university_id}`);
              setUniObj({ id: u.id, name: u.name });
            } catch { /* silent */ }
          }
          if (pr.current_degree_id) {
            try {
              const { data: d } = await api.get(`/degrees/${pr.current_degree_id}`);
              setCurrentDegObj({ id: d.id, title: d.official_title || d.title || d.id });
            } catch { /* silent */ }
          }
          if (pr.target_degree_ids?.length) {
            const rows = await Promise.all(pr.target_degree_ids.map(async (did) => {
              try {
                const { data: d } = await api.get(`/degrees/${did}`);
                return { id: d.id, title: d.official_title || d.title || d.id, university_id: d.university_id };
              } catch {
                return { id: did, title: did };
              }
            }));
            setTargetDegs(rows);
          }
          if (pr.target_cities?.length) setTargetCities(pr.target_cities);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load profile");
      }
    })();
  }, [user, authLoading, openSignIn]);

  // Always fetch the student's shortlist so the "add another target
  // degree" university input can offer their curated unis as
  // quick-pick chips — independent of the target-degree prefill
  // effect below, which only runs in `applying` mode.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: s } = await api.get("/shortlist");
        if (cancelled) return;
        const seen = new Set();
        const unis = [];
        for (const x of (s.items || [])) {
          const u = x.university;
          if (u?.id && !seen.has(u.id)) {
            seen.add(u.id);
            unis.push({ id: u.id, name: u.name });
          }
        }
        setShortlistUnis(unis);
      } catch { /* silent — chips just don't render */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ── Prefill target degrees + cities + graduation year from shortlist ──
  useEffect(() => {
    if (!user || shortlistLoaded) return;
    if (form.status !== "applying") return;
    // Skip prefill only if the saved profile ALREADY has all three
    // things set — otherwise we top up what's missing.
    const prof = data?.profile || {};
    const hasDegs = (prof.target_degree_ids || []).length > 0;
    const hasCities = (prof.target_cities || []).length > 0;
    const hasGrad = !!prof.graduation_year;
    if (hasDegs && hasCities && hasGrad) {
      setShortlistLoaded(true);
      return;
    }
    (async () => {
      try {
        const { data: s } = await api.get("/shortlist");
        const shortRows = (s.items || []).filter(x => x.degree);
        // 1. Target degrees (only if not already stored)
        if (!hasDegs) {
          const items = shortRows.slice(0, 5).map(x => ({
            id: x.degree.id,
            title: x.degree.official_title || x.degree.title || x.degree.id,
            university_id: x.degree.university_id,
          }));
          if (items.length) setTargetDegs(items);
        }
        // 2. Target cities — dedupe from uni.city
        if (!hasCities) {
          const cities = [];
          for (const x of shortRows) {
            const c = (x.university?.city || "").trim();
            if (c && !cities.includes(c)) cities.push(c);
            if (cities.length >= 3) break;
          }
          if (cities.length) setTargetCities(cities);
        }
        // 3. Graduation year = earliest start_year + typical degree length
        //    (UK=3, else 4). If no start_year present, skip.
        if (!hasGrad) {
          const years = shortRows.map(x => x.degree?.start_year).filter(Boolean);
          if (years.length) {
            const earliest = Math.min(...years);
            const anyUKUni = shortRows.some(x => (x.university?.country || "").toUpperCase() === "UK");
            const deltaYears = anyUKUni ? 3 : 4;
            setForm((f) => ({ ...f, graduation_year: String(earliest + deltaYears) }));
          }
        }
        // Toast is only fired when we actually filled something.
        const filled = [];
        if (!hasDegs) filled.push("target degrees");
        if (!hasCities) filled.push("cities");
        if (!hasGrad) filled.push("graduation year");
        if (filled.length && shortRows.length) {
          toast.success(`Prefilled ${filled.join(", ")} from your shortlist`);
        }
        setShortlistLoaded(true);
      } catch { setShortlistLoaded(true); }
    })();
  }, [user, form.status, shortlistLoaded, data]);

  const profile = data?.profile;
  const isAvailable = !!profile?.is_available;

  const canPublish = useMemo(() => {
    const nSkills = (form.skills || []).filter(s => s.trim()).length;
    return nSkills >= 1 && (form.sectors || []).length >= 1 && (form.bio || "").trim().length >= 20;
  }, [form]);

  const bioTooShort = (form.bio || "").trim().length < 20;

  // ── Handlers ────────────────────────────────────────────────────
  const toggleSector = (s) => setForm((f) => {
    const has = f.sectors.includes(s);
    return { ...f, sectors: has ? f.sectors.filter(x => x !== s) : [...f.sectors, s].slice(0, 5) };
  });

  const toggleNotable = (k) => setForm((f) => {
    const has = f.notable_extras.includes(k);
    return { ...f, notable_extras: has ? f.notable_extras.filter(x => x !== k) : [...f.notable_extras, k].slice(0, 6) };
  });

  const toggleDiversity = (k) => setForm((f) => {
    const has = f.diversity_tags.includes(k);
    return { ...f, diversity_tags: has ? f.diversity_tags.filter(x => x !== k) : [...f.diversity_tags, k].slice(0, 8) };
  });

  const setLanguage = (i, patch) => setForm((f) => {
    const langs = [...f.languages];
    langs[i] = { ...langs[i], ...patch };
    return { ...f, languages: langs };
  });

  const addLanguage = () => setForm((f) => (
    f.languages.length >= 5 ? f : { ...f, languages: [...f.languages, { lang: "", level: "fluent" }] }
  ));

  const removeLanguage = (i) => setForm((f) => ({
    ...f, languages: f.languages.filter((_, idx) => idx !== i),
  }));

  const removeTarget = (id) => setTargetDegs((t) => t.filter(x => x.id !== id));

  const [addTarget, setAddTarget] = useState({ uni: null, deg: null });
  const commitAddTarget = () => {
    if (!addTarget.deg?.id) return;
    if (targetDegs.some(x => x.id === addTarget.deg.id)) {
      toast.error("Already in your list");
      return;
    }
    if (targetDegs.length >= 5) {
      toast.error("Max 5 target degrees");
      return;
    }
    setTargetDegs((t) => [...t, { id: addTarget.deg.id, title: addTarget.deg.title, university_id: addTarget.uni?.id }]);
    setAddTarget({ uni: null, deg: null });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        university_id: form.status === "current" ? (uniObj?.id || null) : null,
        current_degree_id: form.status === "current" ? (currentDegObj?.id || null) : null,
        target_degree_ids: form.status === "applying" ? targetDegs.map(d => d.id) : [],
        target_cities: form.status === "applying" ? targetCities : [],
        course_hint: null,
        graduation_year: form.graduation_year ? Number(form.graduation_year) : null,
        skills: (form.skills || []).map(s => s.trim()).filter(Boolean).slice(0, 3),
        sectors: form.sectors,
        subject_families: form.subject_families,
        languages: (form.languages || []).filter(l => (l.lang || "").trim()).map(l => ({ lang: l.lang.trim(), level: l.level })),
        linkedin_url: form.linkedin_url.trim() || null,
        notable_extras: form.notable_extras,
        diversity_tags: form.diversity_tags,
        diversity_reachout_opt_in: !!form.diversity_reachout_opt_in,
        bio: form.bio.trim(),
      };
      const { data: r } = await api.post("/talent/profile", payload);
      setData({
        profile: r.profile,
        allowed_sectors: allowedSectors,
        allowed_notable_extras: allowedNotable,
        allowed_language_levels: allowedLevels,
      });
      toast.success("Profile saved");
    } catch (e) {
      const raw = e?.response?.data?.detail;
      if (raw === "linkedin_url_invalid") {
        toast.error("LinkedIn URL must contain linkedin.com/in/ or linkedin.com/company/");
      } else {
        toast.error(typeof raw === "string" ? raw : "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const setAvailability = async (v) => {
    try {
      await api.post("/talent/profile/publish", { available: v });
      setData((d) => ({ ...d, profile: { ...(d?.profile || {}), is_available: v } }));
      toast.success(v ? "You're now visible to Verified Partners" : "Visibility paused");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (msg === "profile_incomplete") {
        toast.error("Need at least 1 skill, 1 sector and a 20+ character note first");
      } else {
        toast.error(msg || "Failed to update");
      }
    }
  };

  const withdrawInterest = async (id) => {
    try {
      await api.delete(`/talent/interest?id=${encodeURIComponent(id)}`);
      const { data: i } = await api.get("/talent/interests/mine");
      setInterests(i);
      toast.success("Withdrawn");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // ── Render ──────────────────────────────────────────────────────
  if (authLoading || !data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2 text-stone-500" />
        <span className="label-uppercase inline">Loading your profile…</span>
      </div>
    );
  }
  if (!user) return null;

  const asStudent = interests?.as_student || [];
  const mutuals = asStudent.filter(r => r.status === "mutual");

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="talent-profile-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900"
        data-testid="talent-profile-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <header className="mt-3">
        <div className="flex items-center gap-2 text-[#4C1D95]">
          <Handshake className="h-4 w-4" />
          <span className="text-[10px] font-underground-sc tracking-widest">
            Reverse Job Board · Beta
          </span>
        </div>
        <h1 className="mt-1 font-editorial text-3xl sm:text-4xl">
          Let firms find you — on your terms
        </h1>
        <p className="mt-2 text-sm text-stone-600 max-w-2xl">
          Turn this on and Verified Partner firms (Goldman, Freshfields,
          Deloitte etc.) can see your anonymized profile. Names and
          email stay hidden until{" "}
          <span className="font-medium text-stone-900">you both</span>{" "}
          say yes.
        </p>
      </header>

      {ticket && (
        <div className="py-10 mt-6" data-testid="talent-journey-ticket-wrapper">
          <MetroTicket
            data={ticket}
            publicUrl={ticket.ticket_id ? `/journey/${ticket.ticket_id}` : null}
            vertical={false}
            maxScale={1.3}
          />
        </div>
      )}

      {/* Availability toggle */}
      <section
        className="mt-6 p-5 rounded-xl border border-stone-200 bg-white flex items-center justify-between gap-4 flex-wrap"
        data-testid="talent-availability-card"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ShieldCheck className={`h-4 w-4 ${isAvailable ? "text-emerald-700" : "text-stone-400"}`} />
            <div className="font-medium text-stone-900">
              {isAvailable ? "You are visible to Verified Partners" : "You are private"}
            </div>
          </div>
          <div className="mt-1 text-[12px] text-stone-500">
            {isAvailable
              ? "Firms can request an intro. Your name & email only reveal when you say yes back."
              : "Fill in the fields below, then flip this on when you're ready."}
          </div>
        </div>
        <Switch
          checked={isAvailable}
          onCheckedChange={setAvailability}
          disabled={!profile || !canPublish}
          data-testid="talent-availability-toggle"
        />
      </section>

      {/* Mutual matches strip */}
      {mutuals.length > 0 && (
        <section
          className="mt-6 p-5 rounded-xl border border-emerald-200 bg-emerald-50/60"
          data-testid="talent-mutuals"
        >
          <div className="flex items-center gap-2 text-emerald-800">
            <Sparkles className="h-4 w-4" />
            <span className="text-[10px] font-underground-sc tracking-widest">
              Mutual matches
            </span>
          </div>
          <ul className="mt-2 space-y-2">
            {mutuals.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <div className="font-medium text-stone-900 truncate">
                    {m.revealed?.firm_name || m.firm_slug}
                  </div>
                  <div className="text-[11px] font-mono text-stone-500">
                    Matched {fmt(m.matched_at)}
                    {m.revealed?.website && (
                      <>
                        {" · "}
                        <a
                          href={m.revealed.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-800 underline"
                        >
                          website
                        </a>
                      </>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => withdrawInterest(m.id)}
                  data-testid={`talent-mutual-withdraw-${m.firm_slug}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Firm invites (diversity broadcasts) — visible only if student
          opted in and has at least one broadcast targeting their tags */}
      {invites.length > 0 && (
        <section
          className="mt-6 p-5 rounded-xl border border-[#4C1D95]/25 bg-[#F5F0FF]/30"
          data-testid="talent-invites"
        >
          <div className="flex items-center gap-2 text-[#4C1D95]">
            <Sparkles className="h-4 w-4" />
            <span className="text-[10px] font-underground-sc tracking-widest">
              Firm invites · {invites.length}
            </span>
          </div>
          <p className="mt-1 text-[12px] text-stone-600">
            Firms sent these to you because you opted in for a
            diversity-focused programme. Reply "I'm interested" to mutual-match
            and reveal contacts on both sides.
          </p>
          <ul className="mt-3 space-y-3" data-testid="talent-invites-list">
            {invites.map((b) => (
              <li
                key={b.id}
                className="p-3 rounded-lg bg-white border border-stone-200"
                data-testid={`talent-invite-${b.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-stone-900 truncate">
                      {b.firm_name || b.firm_slug}
                    </div>
                    <div className="text-[10px] font-mono text-stone-500">
                      {fmt(b.created_at)}
                    </div>
                  </div>
                  {b.my_status === "mutual" ? (
                    <span className="rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 text-[10px]">
                      Matched
                    </span>
                  ) : b.my_status === "pending" ? (
                    <span className="rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 text-[10px]">
                      They're waiting
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 text-[13px] text-stone-700 whitespace-pre-wrap leading-relaxed line-clamp-4">
                  {b.message}
                </p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {b.programme_url && (
                    <a
                      href={b.programme_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-[#4C1D95] underline"
                      data-testid={`talent-invite-${b.id}-url`}
                    >
                      See the programme →
                    </a>
                  )}
                  {b.my_status !== "mutual" && (
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const { data: r } = await api.post("/talent/interest/student", {
                            firm_slug: b.firm_slug,
                          });
                          toast.success(r.mutual ? "Mutual match!" : "Interest sent");
                          const { data: i } = await api.get("/talent/interests/mine");
                          setInterests(i);
                          const { data: bb } = await api.get("/me/broadcasts");
                          setInvites(bb?.items || []);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed");
                        }
                      }}
                      className="ml-auto bg-[#4C1D95] hover:bg-[#3B0764] text-white h-7 text-[11px]"
                      data-testid={`talent-invite-${b.id}-reply`}
                    >
                      I'm interested
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section
        className="mt-6 p-5 rounded-xl border border-stone-200 bg-white space-y-5"
        data-testid="talent-profile-form"
      >
        {/* Status toggle */}
        <div>
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-2">
            Where you are
          </div>
          <div className="inline-flex rounded-full border border-stone-300 p-0.5">
            {[
              { k: "current", label: "I'm at university" },
              { k: "applying", label: "I'm applying" },
            ].map(({ k, label }) => {
              const on = form.status === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setForm({ ...form, status: k })}
                  data-testid={`talent-status-${k}`}
                  className={
                    "px-4 py-1.5 rounded-full text-[13px] transition " +
                    (on
                      ? "bg-[#4C1D95] text-white"
                      : "text-stone-700 hover:bg-stone-50")
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {form.status === "current" ? (
          <div className="space-y-3">
            <label className="text-sm block">
              <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">
                University
              </div>
              <UniversityAutocomplete
                value={uniObj}
                onSelect={(u) => { setUniObj(u); if (!u) setCurrentDegObj(null); }}
                testIdPrefix="talent-uni-ac"
              />
            </label>
            <label className="text-sm block">
              <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">
                Course / degree
              </div>
              <DegreeAutocomplete
                universityId={uniObj?.id}
                value={currentDegObj}
                onSelect={setCurrentDegObj}
                testIdPrefix="talent-current-deg-ac"
              />
            </label>
          </div>
        ) : (
          <div className="space-y-3" data-testid="talent-target-degrees">
            <div className="text-[11px] font-underground-sc tracking-widest text-stone-500">
              Target degrees ({targetDegs.length}/5) · prefilled from your shortlist
            </div>
            {targetDegs.length === 0 ? (
              <p className="text-[12px] text-stone-500">
                No targets yet.{" "}
                <Link to="/explore" className="underline">Explore courses</Link>{" "}
                or add one below.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {targetDegs.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-start justify-between gap-2 p-2 rounded-md border border-stone-200 bg-stone-50"
                    data-testid={`talent-target-${d.id}`}
                  >
                    <div className="min-w-0 text-sm">
                      <GraduationCap className="inline h-3.5 w-3.5 text-stone-500 mr-1.5 -mt-0.5" />
                      <span className="text-stone-900">{d.title}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeTarget(d.id)}
                      className="text-stone-400 hover:text-red-600 p-0.5"
                      aria-label="Remove"
                      data-testid={`talent-target-remove-${d.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {targetDegs.length < 5 && (
              <div
                className="grid sm:grid-cols-[1fr,1fr,auto] gap-2 items-end p-3 rounded-md border border-dashed border-stone-300"
                data-testid="talent-target-add"
              >
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
                      University
                    </div>
                    {shortlistUnis.length > 0 && (
                      <div className="text-[10px] font-mono text-stone-400">
                        from your shortlist ↓
                      </div>
                    )}
                  </div>
                  {shortlistUnis.length > 0 && (
                    <div
                      className="mb-2 flex flex-wrap gap-1"
                      data-testid="talent-add-target-uni-quickpicks"
                    >
                      {shortlistUnis.map((u) => {
                        const on = addTarget.uni?.id === u.id;
                        return (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => setAddTarget({ uni: u, deg: null })}
                            data-testid={`talent-add-target-uni-quickpick-${u.id}`}
                            className={
                              "rounded-full border px-2.5 py-1 text-[11px] transition " +
                              (on
                                ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                                : "bg-white text-stone-700 border-stone-300 hover:border-[#4C1D95] hover:text-[#4C1D95]")
                            }
                            title={`Pick ${u.name}`}
                          >
                            {u.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <UniversityAutocomplete
                    value={addTarget.uni}
                    onSelect={(u) => setAddTarget({ uni: u, deg: null })}
                    testIdPrefix="talent-add-target-uni-ac"
                    placeholder={
                      shortlistUnis.length > 0
                        ? "…or search another university"
                        : "Search university…"
                    }
                  />
                </div>
                <div>
                  <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-1">
                    Degree
                  </div>
                  <DegreeAutocomplete
                    universityId={addTarget.uni?.id}
                    value={addTarget.deg}
                    onSelect={(d) => setAddTarget((s) => ({ ...s, deg: d }))}
                    testIdPrefix="talent-add-target-deg-ac"
                  />
                </div>
                <Button
                  onClick={commitAddTarget}
                  disabled={!addTarget.deg}
                  data-testid="talent-target-add-commit"
                  className="bg-[#4C1D95] hover:bg-[#3B0764] text-white h-10"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
            )}

            {/* Target cities — prefilled from shortlist uni.city, editable */}
            <div data-testid="talent-target-cities">
              <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-1">
                Target cities ({targetCities.length}/3) · prefilled from your shortlist
              </div>
              <CityAutocomplete
                multi
                values={targetCities}
                onChange={(next) => setTargetCities(next.slice(0, 3))}
                maxChips={3}
                placeholder="+ add city"
                testIdPrefix="talent-target-city"
              />
            </div>
          </div>
        )}

        <label className="text-sm block sm:w-1/2">
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">
            Graduation year
          </div>
          {/* Dropdown covers the realistic student cohort — past 5
              years (already-graduated alumni polishing their profile),
              this year, and the next 4 years (in-progress students
              planning their first roles). */}
          <Select
            value={form.graduation_year ? String(form.graduation_year) : ""}
            onValueChange={(v) => setForm({ ...form, graduation_year: v })}
          >
            <SelectTrigger
              data-testid="talent-grad-year-input"
              className="w-full"
            >
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {(() => {
                const thisYear = new Date().getFullYear();
                const years = [];
                for (let y = thisYear - 5; y <= thisYear + 4; y++) years.push(y);
                return years.map((y) => (
                  <SelectItem
                    key={y}
                    value={String(y)}
                    data-testid={`talent-grad-year-option-${y}`}
                  >
                    {y}
                  </SelectItem>
                ));
              })()}
            </SelectContent>
          </Select>
        </label>

        <div>
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">
            Top 3 skills
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <Input
                key={i}
                value={form.skills[i] || ""}
                onChange={(e) => {
                  const s = [...form.skills];
                  s[i] = e.target.value;
                  setForm({ ...form, skills: s });
                }}
                placeholder={["Python", "Financial modelling", "Public speaking"][i]}
                maxLength={32}
                data-testid={`talent-skill-input-${i}`}
              />
            ))}
          </div>
        </div>

        {/* Subject / degree families — freshers who haven't picked a
            specific degree still get discovered by firms searching
            broad academic buckets. */}
        <div data-testid="talent-subject-families">
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">
            Subject families ({(form.subject_families || []).length}/5)
          </div>
          <div className="text-[11px] text-stone-500 mb-1.5">
            Pick the broad discipline(s) you study, target, or are
            transferring into. Recruiters filter by these before
            they know your exact course.
          </div>
          <div className="flex flex-wrap gap-1.5">
            {subjectFamilies.map((f) => {
              const on = (form.subject_families || []).includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setForm((prev) => {
                    const list = prev.subject_families || [];
                    const next = list.includes(f.key)
                      ? list.filter((x) => x !== f.key)
                      : [...list, f.key].slice(0, 5);
                    return { ...prev, subject_families: next };
                  })}
                  data-testid={`talent-subject-family-${f.key}`}
                  className={
                    "rounded-full border px-3 py-1 text-[12px] transition " +
                    (on
                      ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                      : "bg-white text-stone-700 border-stone-300 hover:border-stone-400")
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">
            Target sectors (pick up to 5)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {allowedSectors.map((s) => {
              const on = form.sectors.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSector(s)}
                  data-testid={`talent-sector-${s}`}
                  className={
                    "rounded-full border px-3 py-1 text-[12px] transition " +
                    (on
                      ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                      : "bg-white text-stone-700 border-stone-300 hover:border-stone-400")
                  }
                >
                  {SECTOR_LABELS[s] || s}
                </button>
              );
            })}
          </div>
        </div>

        {/* Languages */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] font-underground-sc tracking-widest text-stone-500">
              Languages ({form.languages.length}/5)
            </div>
            {form.languages.length < 5 && (
              <button
                type="button"
                onClick={addLanguage}
                className="text-[11px] text-[#4C1D95] hover:underline"
                data-testid="talent-language-add"
              >
                + add language
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            {form.languages.map((l, i) => (
              <div
                key={i}
                className="grid grid-cols-[1fr,140px,32px] gap-2"
                data-testid={`talent-language-row-${i}`}
              >
                <Input
                  value={l.lang}
                  onChange={(e) => setLanguage(i, { lang: e.target.value })}
                  placeholder={i === 0 ? "English" : "Spanish, Mandarin…"}
                  maxLength={32}
                  data-testid={`talent-language-name-${i}`}
                />
                <select
                  value={l.level}
                  onChange={(e) => setLanguage(i, { level: e.target.value })}
                  className="h-10 rounded-md border border-stone-300 bg-white px-2 text-sm"
                  data-testid={`talent-language-level-${i}`}
                >
                  {allowedLevels.map(lv => (
                    <option key={lv} value={lv}>{lv[0].toUpperCase() + lv.slice(1)}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeLanguage(i)}
                  disabled={form.languages.length <= 1}
                  className="text-stone-400 hover:text-red-600 disabled:opacity-30 flex items-center justify-center"
                  data-testid={`talent-language-remove-${i}`}
                  aria-label="Remove language"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* LinkedIn */}
        <label className="block">
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1 flex items-center gap-1.5">
            <Linkedin className="h-3 w-3" />
            LinkedIn URL (optional)
          </div>
          <Input
            value={form.linkedin_url}
            onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
            placeholder="https://linkedin.com/in/yourname"
            data-testid="talent-linkedin-input"
          />
        </label>

        {/* Notable extras */}
        <div>
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1">
            Notable extras (pick up to 6 — tie-breakers for firms)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {allowedNotable.map((k) => {
              const on = form.notable_extras.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleNotable(k)}
                  data-testid={`talent-notable-${k}`}
                  className={
                    "rounded-full border px-3 py-1 text-[12px] transition " +
                    (on
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-white text-stone-700 border-stone-300 hover:border-stone-400")
                  }
                >
                  {NOTABLE_LABELS[k] || k}
                </button>
              );
            })}
          </div>
        </div>

        {/* Diversity — self-declared, optional, private by default */}
        <div
          className="rounded-xl border border-stone-200 bg-[#FAF9F5] p-4 space-y-3"
          data-testid="talent-diversity-section"
        >
          <div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] font-underground-sc tracking-widest text-stone-700">
                Diversity tags · optional · {form.diversity_tags.length}/8
              </div>
              <span className="text-[10px] font-mono text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                Private by default
              </span>
            </div>
            <p className="mt-1 text-[12px] text-stone-600 leading-relaxed">
              These are only used if you also toggle the opt-in below.
              Firms never see individual tags on your anonymized card —
              tags reveal only when you and a firm mutually match AND
              you've opted in below. Delete any tag any time.
            </p>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {allowedDiversity.map((k) => {
              const on = form.diversity_tags.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleDiversity(k)}
                  data-testid={`talent-diversity-${k}`}
                  className={
                    "rounded-full border px-3 py-1 text-[12px] transition " +
                    (on
                      ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                      : "bg-white text-stone-700 border-stone-300 hover:border-stone-400")
                  }
                >
                  {DIVERSITY_LABELS[k] || k}
                </button>
              );
            })}
          </div>

          <label
            className="flex items-start gap-2.5 pt-2 border-t border-stone-200 cursor-pointer"
            data-testid="talent-diversity-optin-label"
          >
            <Switch
              checked={!!form.diversity_reachout_opt_in}
              onCheckedChange={(v) => setForm((f) => ({ ...f, diversity_reachout_opt_in: !!v }))}
              data-testid="talent-diversity-optin-switch"
              disabled={form.diversity_tags.length === 0}
              className="mt-0.5 data-[state=checked]:bg-[#4C1D95]"
            />
            <div className="text-[12px] text-stone-700 leading-relaxed">
              <span className="font-medium">
                Let Verified Partner firms invite me to diversity-focused programmes
              </span>
              <span className="block text-[11px] text-stone-500 mt-0.5">
                Firms searching for these tags will see your anonymized
                card in their results. Your tags only reveal to a firm
                after you both mutually match. Toggle off any time to
                disappear from these searches instantly.
              </span>
              {form.diversity_tags.length === 0 && (
                <span className="block text-[11px] text-amber-700 mt-1">
                  Add at least one tag above to enable this.
                </span>
              )}
            </div>
          </label>
        </div>

        {/* Bio */}
        <label className="block">
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500 mb-1 flex items-center justify-between gap-2">
            <span>What you're looking for ({(form.bio || "").length}/200 · min 20)</span>
            <button
              type="button"
              onClick={async () => {
                if (draftingBio) return;
                setDraftingBio(true);
                try {
                  const { data } = await api.post("/talent/profile/bio/draft");
                  const draft = (data?.draft || "").trim();
                  if (!draft) {
                    toast.error("No draft returned — try again.");
                    return;
                  }
                  setForm((f) => ({ ...f, bio: draft.slice(0, 200) }));
                  toast.success("Draft ready — edit it in your voice, then Save profile.");
                } catch (e) {
                  const msg = e?.response?.data?.detail || "Could not draft — try again.";
                  toast.error(String(msg));
                } finally {
                  setDraftingBio(false);
                }
              }}
              disabled={draftingBio}
              data-testid="talent-bio-ai-draft"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-underground-sc tracking-widest uppercase border border-[#4C1D95] text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {draftingBio
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Sparkles className="h-3 w-3" />}
              {draftingBio ? "Drafting…" : "Draft with AI"}
            </button>
          </div>
          <Textarea
            rows={4}
            maxLength={200}
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            placeholder="Second-year physics, keen on quant research or climate-tech ventures. Interned at a hedge fund summer 2025…"
            data-testid="talent-bio-textarea"
          />
          <div className="mt-1 text-[10px] text-stone-500">
            AI drafts from your sectors, subjects and career direction — always edit before saving so it sounds like you.
          </div>
          {bioTooShort && (form.bio || "").length > 0 && (
            <div className="mt-1 text-[10px] text-amber-700">
              {20 - (form.bio || "").trim().length} more character{20 - (form.bio || "").trim().length === 1 ? "" : "s"} to publish
            </div>
          )}
        </label>

        <div className="flex items-center justify-between pt-2">
          <div className="flex flex-wrap gap-1.5">
            {(form.sectors || []).map((s) => (
              <Badge key={s} variant="outline">{SECTOR_LABELS[s] || s}</Badge>
            ))}
          </div>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#4C1D95] hover:bg-[#3B0764] text-white"
            data-testid="talent-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Save profile
          </Button>
        </div>
      </section>

      <p className="mt-6 text-[11px] font-underground-sc tracking-wider text-stone-400">
        Privacy: your profile is opt-in and anonymized on the firm side.
        Only <Link to="/for-employers" className="underline">Verified Partner firms</Link>{" "}
        on the Mid or Large plan can browse. Withdraw a match at any time.
      </p>
    </div>
  );
}
